import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { collection, query, where, getDocs, orderBy, getDoc, doc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { useFirebase } from '@/contexts/FirebaseContext';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, Download, FileText, BookOpen } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Skill {
  name: string;
  rating: number;
  notes?: string;
}

interface Lesson {
  id: string;
  date: string;
  time: string;
  duration: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  instructorName: string;
  instructorEmail: string;
  courseName: string;
  courseId: string;
  enrollmentId: string;
  venue: string;
  notes?: string;
  progress?: string;
  feedback?: string;
  skills?: Skill[];
  topics?: string[];
  nextSteps?: string[];
}

interface Enrollment {
  id: string;
  courseName: string;
  courseId: string;
  instructorName: string;
  instructorEmail: string;
}

const LessonHistory = () => {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useFirebase();
  const { toast } = useToast();

  useEffect(() => {
    const fetchLessons = async () => {
      if (!currentUser?.uid) return;

      try {
        setLoading(true);
        const db = getFirestore();
        
        // First, get all enrollments for the student
        const enrollmentsRef = collection(db, 'enrollments');
        const enrollmentsQuery = query(
          enrollmentsRef,
          where('userId', '==', currentUser.uid)
        );
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        
        // Create a map of enrollment IDs to enrollment data
        const enrollmentMap = new Map<string, Enrollment>();
        enrollmentsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          enrollmentMap.set(doc.id, {
            id: doc.id,
            courseName: data.courseName || 'N/A',
            courseId: data.courseId || '',
            instructorName: data.instructorName || 'N/A',
            instructorEmail: data.instructorEmail || ''
          });
        });

        // Fetch all schedules for the student
        const schedulesRef = collection(db, 'schedules');
        const schedulesQuery = query(
          schedulesRef,
          where('studentId', '==', currentUser.uid),
          where('status', 'in', ['completed', 'cancelled'])
        );
        const schedulesSnapshot = await getDocs(schedulesQuery);
        
        const lessonsData: Lesson[] = [];
        
        // Process each schedule
        for (const doc of schedulesSnapshot.docs) {
          const scheduleData = doc.data();
          const enrollment = enrollmentMap.get(scheduleData.enrollmentId);
          
          lessonsData.push({
            id: doc.id,
            date: scheduleData.date,
            time: scheduleData.startTime,
            duration: scheduleData.duration,
            status: scheduleData.status,
            instructorName: scheduleData.instructorName || enrollment?.instructorName || 'N/A',
            instructorEmail: scheduleData.instructorEmail || enrollment?.instructorEmail || '',
            courseName: enrollment?.courseName || scheduleData.courseName || 'N/A',
            courseId: enrollment?.courseId || scheduleData.courseId || '',
            enrollmentId: scheduleData.enrollmentId || '',
            venue: scheduleData.venue || 'Not specified',
            notes: scheduleData.notes,
            progress: scheduleData.progress,
            feedback: scheduleData.feedback,
            skills: scheduleData.skills,
            topics: scheduleData.topics,
            nextSteps: scheduleData.nextSteps
          });
        }

        // Sort lessons by date in descending order
        lessonsData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        setLessons(lessonsData);
      } catch (error) {
        console.error('Error fetching lessons:', error);
        toast({
          title: 'Error',
          description: 'Failed to load lesson history. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchLessons();
  }, [currentUser, toast]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    let yPosition = 20;

    // Add header with logo placeholder
    doc.setFillColor(41, 128, 185);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Driving Lesson Report', pageWidth / 2, 25, { align: 'center' });

    // Reset text color for content
    doc.setTextColor(0, 0, 0);
    yPosition = 50;

    // Student Information Section
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Student Information', margin, yPosition);
    yPosition += 10;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${currentUser?.displayName || 'N/A'}`, margin, yPosition);
    yPosition += 7;
    doc.text(`Email: ${currentUser?.email || 'N/A'}`, margin, yPosition);
    yPosition += 7;
    doc.text(`Report Generated: ${format(new Date(), 'MMMM d, yyyy')}`, margin, yPosition);
    yPosition += 15;

    // Lessons Summary Table
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Lessons Summary', margin, yPosition);
    yPosition += 10;

    const tableColumn = ['Date', 'Time', 'Duration', 'Status', 'Instructor', 'Course', 'Venue'];
    const tableRows = lessons.map(lesson => [
      format(new Date(lesson.date), 'MMM d, yyyy'),
      lesson.time,
      `${lesson.duration} hour${lesson.duration > 1 ? 's' : ''}`,
      lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1),
      lesson.instructorName,
      lesson.courseName,
      lesson.venue
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: yPosition,
      theme: 'grid',
      styles: { 
        fontSize: 9,
        cellPadding: 3,
        lineColor: [41, 128, 185],
        lineWidth: 0.1
      },
      headStyles: { 
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 20 },
        2: { cellWidth: 20 },
        3: { cellWidth: 25 },
        4: { cellWidth: 35 },
        5: { cellWidth: 35 },
        6: { cellWidth: 35 }
      }
    });

    // Detailed Lesson Information
    yPosition = (doc as any).lastAutoTable.finalY + 15;
    
    lessons.forEach((lesson, index) => {
      // Check if we need a new page
      if (yPosition > doc.internal.pageSize.getHeight() - 40) {
        doc.addPage();
        yPosition = 20;
      }

      if (lesson.notes || lesson.feedback || lesson.progress || lesson.skills || lesson.topics || lesson.nextSteps) {
        // Lesson Header
        doc.setFillColor(41, 128, 185);
        doc.rect(margin - 2, yPosition - 5, pageWidth - (margin * 2) + 4, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Lesson ${index + 1} - ${format(new Date(lesson.date), 'MMMM d, yyyy')}`, margin, yPosition);
        yPosition += 10;

        // Reset text color for content
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        // Lesson Details
        const details = [
          { label: 'Time', value: `${lesson.time} (${lesson.duration} hour${lesson.duration > 1 ? 's' : ''})` },
          { label: 'Instructor', value: lesson.instructorName },
          { label: 'Course', value: lesson.courseName },
          { label: 'Venue', value: lesson.venue }
        ];

        details.forEach(detail => {
          doc.setFont('helvetica', 'bold');
          doc.text(`${detail.label}:`, margin, yPosition);
          doc.setFont('helvetica', 'normal');
          doc.text(detail.value, margin + 30, yPosition);
          yPosition += 7;
        });

        yPosition += 5;

        // Notes
        if (lesson.notes) {
          doc.setFont('helvetica', 'bold');
          doc.text('Notes:', margin, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          const notesLines = doc.splitTextToSize(lesson.notes, pageWidth - (margin * 2));
          doc.text(notesLines, margin, yPosition);
          yPosition += (notesLines.length * 7) + 5;
        }

        // Progress
        if (lesson.progress) {
          doc.setFont('helvetica', 'bold');
          doc.text('Progress:', margin, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          const progressLines = doc.splitTextToSize(lesson.progress, pageWidth - (margin * 2));
          doc.text(progressLines, margin, yPosition);
          yPosition += (progressLines.length * 7) + 5;
        }

        // Feedback
        if (lesson.feedback) {
          doc.setFont('helvetica', 'bold');
          doc.text('Feedback:', margin, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          const feedbackLines = doc.splitTextToSize(lesson.feedback, pageWidth - (margin * 2));
          doc.text(feedbackLines, margin, yPosition);
          yPosition += (feedbackLines.length * 7) + 5;
        }

        // Skills
        if (lesson.skills && lesson.skills.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text('Skills Assessed:', margin, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          lesson.skills.forEach(skill => {
            const skillText = `• ${skill.name}: ${skill.rating}/5${skill.notes ? ` (${skill.notes})` : ''}`;
            doc.text(skillText, margin + 5, yPosition);
            yPosition += 7;
          });
          yPosition += 5;
        }

        // Topics
        if (lesson.topics && lesson.topics.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text('Topics Covered:', margin, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          lesson.topics.forEach(topic => {
            doc.text(`• ${topic}`, margin + 5, yPosition);
            yPosition += 7;
          });
          yPosition += 5;
        }

        // Next Steps
        if (lesson.nextSteps && lesson.nextSteps.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.text('Next Steps:', margin, yPosition);
          yPosition += 7;
          doc.setFont('helvetica', 'normal');
          lesson.nextSteps.forEach(step => {
            doc.text(`• ${step}`, margin + 5, yPosition);
            yPosition += 7;
          });
        }

        yPosition += 15;
      }
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Save the PDF
    doc.save('driving-lessons-report.pdf');
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex items-center justify-center h-64">
          <p>Loading lessons...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">My Lessons</h1>
            <p className="text-gray-600 mt-2">View and download your complete lesson history</p>
          </div>
          <Button
            onClick={generatePDF}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download Report
          </Button>
        </div>

        {lessons.length === 0 ? (
          <Card>
            <CardContent className="flex items-center justify-center h-64">
              <p className="text-gray-500">No lessons found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {lessons.map((lesson) => (
              <Card key={lesson.id}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-drivewise-600" />
                      <span className="font-medium">
                        {format(new Date(lesson.date), 'EEEE, MMMM d, yyyy')}
                      </span>
                    </div>
                    <Badge className={getStatusColor(lesson.status)}>
                      {lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1)}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span>{lesson.time} ({lesson.duration} {lesson.duration === 1 ? 'hour' : 'hours'})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span>Instructor: {lesson.instructorName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-gray-500" />
                        <span>Course: {lesson.courseName}</span>
                      </div>
                    </div>

                    {(lesson.notes || lesson.progress || lesson.feedback || lesson.skills || lesson.topics || lesson.nextSteps) && (
                      <div className="space-y-2">
                        {lesson.notes && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Notes:</p>
                            <p className="text-sm">{lesson.notes}</p>
                          </div>
                        )}
                        {lesson.progress && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Progress:</p>
                            <p className="text-sm">{lesson.progress}</p>
                          </div>
                        )}
                        {lesson.feedback && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Feedback:</p>
                            <p className="text-sm">{lesson.feedback}</p>
                          </div>
                        )}
                        {lesson.skills && lesson.skills.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Skills Assessed:</p>
                            <div className="space-y-1">
                              {lesson.skills.map((skill, index) => (
                                <div key={index} className="text-sm">
                                  <span className="font-medium">{skill.name}:</span> {skill.rating}/5
                                  {skill.notes && <span className="text-gray-500"> - {skill.notes}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {lesson.topics && lesson.topics.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Topics Covered:</p>
                            <ul className="list-disc list-inside text-sm">
                              {lesson.topics.map((topic, index) => (
                                <li key={index}>{topic}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {lesson.nextSteps && lesson.nextSteps.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-500">Next Steps:</p>
                            <ul className="list-disc list-inside text-sm">
                              {lesson.nextSteps.map((step, index) => (
                                <li key={index}>{step}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LessonHistory; 